// Calorie deficit calculator: pure engine (compute) + DOM binding.
// Same contract as protein-calculator.js: every coefficient and message comes from
// the section's JSON config, so the engine stays testable under Node.
(function (root) {
  'use strict';

  function roundTo(value, step) {
    return Math.round(value / step) * step;
  }

  function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = Number(String(value).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function findById(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) return list[i];
    }
    return null;
  }

  function validate(input, limits) {
    var errors = {};
    var weight = toNumber(input.weight);
    var height = toNumber(input.height);
    var age = toNumber(input.age);
    var goal = toNumber(input.goal);

    if (weight === null || weight < limits.weight_min || weight > limits.weight_max) errors.weight = true;
    if (height === null || height < limits.height_min || height > limits.height_max) errors.height = true;
    if (age === null || age < limits.age_min || age > limits.age_max) errors.age = true;
    if (goal !== null && (goal <= 0 || (weight !== null && goal >= weight))) errors.goal = true;
    if (input.sex !== 'female' && input.sex !== 'male') errors.sex = true;
    if (!input.activity) errors.activity = true;
    if (!input.pace) errors.pace = true;

    return { errors: errors, weight: weight, height: height, age: age, goal: goal };
  }

  function bmr(sex, weight, height, age) {
    // Reason: Mifflin-St Jeor is the most accurate equation for non-obese and obese adults (Frankenfield 2005).
    return 10 * weight + 6.25 * height - 5 * age + (sex === 'male' ? 5 : -161);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  // Reason: dietitian's method. Protein first, then fat, carbs take the rest.
  // Percent-of-energy bounds are hard, g/kg bounds are soft (they give way when both can't hold).
  function macros(target, weight, height, m) {
    var meters = height / 100;
    var refWeight = weight;
    var adjusted = false;
    if (weight / (meters * meters) > m.bmi_threshold) {
      refWeight = Math.round(m.bmi_reference * meters * meters);
      adjusted = true;
    }

    var pMin = target * m.protein_pct_min / 400;
    var pMax = target * m.protein_pct_max / 400;
    var fMin = target * m.fat_pct_min / 900;
    var fMax = target * m.fat_pct_max / 900;
    var cMin = target * m.carbs_pct_min / 400;
    var cMax = target * m.carbs_pct_max / 400;
    var pSoftMin = clamp(refWeight * m.protein_min_kg, pMin, pMax);
    var pSoftMax = clamp(refWeight * m.protein_max_kg, pMin, pMax);
    var fSoftMin = clamp(refWeight * m.fat_min_kg, fMin, fMax);

    var protein = clamp(clamp(refWeight * m.protein_per_kg, refWeight * m.protein_min_kg, refWeight * m.protein_max_kg), pMin, pMax);
    var fat = clamp(Math.max(refWeight * m.fat_min_kg, fMin), fMin, fMax);

    function carbs() {
      return (target - 4 * protein - 9 * fat) / 4;
    }

    var lowSteps = [['p', pSoftMin], ['f', fSoftMin], ['p', pMin], ['f', fMin]];
    for (var i = 0; i < lowSteps.length && carbs() < cMin; i++) {
      var missing = (cMin - carbs()) * 4;
      if (lowSteps[i][0] === 'p') protein = Math.max(lowSteps[i][1], Math.min(protein, protein - missing / 4));
      else fat = Math.max(lowSteps[i][1], Math.min(fat, fat - missing / 9));
    }

    var highSteps = [['f', fMax], ['p', pSoftMax]];
    for (var j = 0; j < highSteps.length && carbs() > cMax; j++) {
      var extra = (carbs() - cMax) * 4;
      if (highSteps[j][0] === 'p') protein = Math.min(highSteps[j][1], Math.max(protein, protein + extra / 4));
      else fat = Math.min(highSteps[j][1], Math.max(fat, fat + extra / 9));
    }

    // Reason: whole grams, without leaving the hard percent window.
    function roundInside(value, min, max) {
      return clamp(Math.round(value), Math.ceil(min), Math.floor(max));
    }
    var p = roundInside(protein, pMin, pMax);
    var f = roundInside(fat, fMin, fMax);
    var c = Math.max(0, Math.round((target - 4 * p - 9 * f) / 4));
    function pct(kcalValue) { return Math.round(kcalValue / target * 100); }

    return {
      protein: { g: p, pct: pct(4 * p) },
      fat: { g: f, pct: pct(9 * f) },
      carbs: { g: c, pct: pct(4 * c) },
      refWeight: refWeight,
      adjusted: adjusted
    };
  }

  function compute(input, config) {
    var checked = validate(input, config.limits);
    if (Object.keys(checked.errors).length) return { status: 'invalid', errors: checked.errors };

    if (checked.age < config.adult_age) return { status: 'blocked', reason: 'minor' };
    if (input.situation && input.situation !== 'none') return { status: 'blocked', reason: input.situation };

    var activity = findById(config.activities, input.activity);
    var pace = findById(config.paces, input.pace);
    if (!activity) return { status: 'invalid', errors: { activity: true } };
    if (!pace) return { status: 'invalid', errors: { pace: true } };

    var meters = checked.height / 100;
    var bmi = checked.weight / (meters * meters);
    if (bmi < config.bmi_min) return { status: 'blocked', reason: 'underweight' };

    var rest = Math.ceil(bmr(input.sex, checked.weight, checked.height, checked.age) / config.step) * config.step;
    var maintenance = roundTo(bmr(input.sex, checked.weight, checked.height, checked.age) * activity.pal, config.step);
    var absolute = input.sex === 'male' ? config.floor_male : config.floor_female;
    var floor = Math.max(rest, absolute);
    var floorKind = rest >= absolute ? 'rest' : 'absolute';

    var requested = roundTo(pace.kg * config.kcal_per_kg / 7, config.step);
    var cap = roundTo(maintenance * config.max_pct / 100, config.step);
    var applied = Math.min(requested, cap);
    var limit = requested > cap ? 'cap' : null;

    if (maintenance - applied < floor) {
      applied = maintenance - floor;
      limit = floorKind;
    }

    var base = {
      maintenance: maintenance,
      rest: rest,
      floor: floor,
      floorKind: floorKind,
      requested: requested,
      pace: pace,
      activity: activity
    };

    if (applied < config.min_deficit) {
      base.status = 'no_room';
      return base;
    }

    var weekly = Math.round(applied * 7 / config.kcal_per_kg * 20) / 20;
    base.status = 'ok';
    base.target = maintenance - applied;
    base.applied = applied;
    base.pct = Math.round(applied / maintenance * 100);
    base.limit = limit;
    base.cap = cap;
    base.weekly = weekly;
    if (config.macros) base.macros = macros(base.target, checked.weight, checked.height, config.macros);

    if (checked.goal !== null) {
      var finalBmi = (checked.weight - checked.goal) / (meters * meters);
      if (finalBmi < config.bmi_min) {
        base.goal = { status: 'too_low', minWeight: Math.ceil(config.bmi_min * meters * meters) };
      } else {
        base.goal = { status: 'ok', kg: checked.goal, weeks: Math.ceil(checked.goal / weekly) };
      }
    }

    return base;
  }

  function ageBand(age, config) {
    if (age >= 65) return '65+';
    if (age >= 40) return '40-64';
    return config.adult_age + '-39';
  }

  function format(template, values) {
    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function kcal(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  function kg(n) {
    return String(n).replace('.', ',');
  }

  // Reason: Cowlendar adds the booking to the cart then shows its own confirmation; send the visitor straight to checkout instead.
  function checkoutAfterBooking() {
    if (checkoutAfterBooking.on || typeof PerformanceObserver === 'undefined') return;
    checkoutAfterBooking.on = true;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (!/\/cart\/add(\.js)?(\?|$)/.test(entry.name) || entry.name.indexOf('cow_upsell') !== -1) return;
          window.fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (res) { return res.json(); })
            .then(function (cart) { if (cart.item_count > 0) window.location.href = root + 'checkout'; })
            .catch(function () {});
        });
      }).observe({ type: 'resource' });
    } catch (e) {}
  }

  function saveAttributes(attributes) {
    if (!window.fetch) return Promise.resolve();
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return window.fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  function init(el) {
    var configNode = el.querySelector('[data-dc-config]');
    var form = el.querySelector('[data-dc-form]');
    var output = el.querySelector('[data-dc-output]');
    if (!configNode || !form || !output) return;

    var config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (e) {
      return;
    }

    var refs = {
      placeholder: el.querySelector('[data-dc-placeholder]'),
      ok: output.querySelector('[data-dc-ok]'),
      noRoom: output.querySelector('[data-dc-no-room]'),
      maintenance: output.querySelectorAll('[data-dc-maintenance]'),
      target: output.querySelector('[data-dc-target]'),
      floor: output.querySelectorAll('[data-dc-floor]'),
      pace: output.querySelector('[data-dc-pace]'),
      limit: output.querySelector('[data-dc-limit]'),
      notes: output.querySelector('[data-dc-notes]'),
      noRoomText: output.querySelector('[data-dc-no-room-text]'),
      blocked: output.querySelectorAll('[data-dc-blocked]'),
      macros: output.querySelector('[data-dc-macros]'),
      macrosNote: output.querySelector('[data-dc-macros-note]'),
      callLink: output.querySelector('[data-dc-call-link]'),
      callMessage: output.querySelector('[data-dc-call-message]'),
      loading: output.querySelector('[data-dc-loading]')
    };
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var run = 0;
    var submitted = false;
    var last = null;

    function read() {
      var data = new FormData(form);
      return {
        sex: data.get('sex'),
        weight: data.get('weight'),
        height: data.get('height'),
        age: data.get('age'),
        activity: data.get('activity'),
        pace: data.get('pace'),
        goal: data.get('goal'),
        situation: data.get('situation') || 'none'
      };
    }

    function showErrors(errors) {
      ['sex', 'weight', 'height', 'age', 'activity', 'pace', 'goal'].forEach(function (name) {
        var field = form.querySelector('[data-dc-field="' + name + '"]');
        if (!field) return;
        var controls = field.querySelectorAll('input, select');
        var invalid = !!errors[name];
        field.classList.toggle('is-invalid', invalid);
        Array.prototype.forEach.call(controls, function (control) {
          if (invalid) control.setAttribute('aria-invalid', 'true');
          else control.removeAttribute('aria-invalid');
        });
      });
    }

    function setAll(nodes, text) {
      Array.prototype.forEach.call(nodes, function (node) { node.textContent = text; });
    }

    function limitText(result) {
      if (!result.limit) return '';
      var values = {
        requested: kcal(result.requested),
        applied: kcal(result.applied),
        target: kcal(result.target),
        rest: kcal(result.rest),
        floor: kcal(result.floor),
        pct: config.max_pct,
        pace: kg(result.pace.kg)
      };
      return format(config.messages['limit_' + result.limit], values);
    }

    function render(result, input) {
      showErrors(result.errors || {});
      if (result.status === 'invalid') return false;
      last = result.status === 'ok' ? { result: result, input: input } : null;

      output.hidden = false;
      if (refs.placeholder) refs.placeholder.hidden = true;
      Array.prototype.forEach.call(refs.blocked, function (node) {
        node.hidden = result.status !== 'blocked' || node.getAttribute('data-dc-blocked') !== result.reason;
      });
      refs.ok.hidden = result.status !== 'ok';
      refs.noRoom.hidden = result.status !== 'no_room';
      if (result.status === 'blocked') return true;

      setAll(refs.maintenance, kcal(result.maintenance));
      setAll(refs.floor, kcal(result.floor));

      if (result.status === 'no_room') {
        refs.noRoomText.textContent = format(config.messages.no_room, {
          maintenance: kcal(result.maintenance),
          floor: kcal(result.floor)
        });
        return true;
      }

      refs.target.textContent = kcal(result.target);
      refs.pace.textContent = format(config.messages.pace, {
        applied: kcal(result.applied),
        pct: result.pct,
        weekly: kg(result.weekly)
      });

      var limit = limitText(result);
      var limitHtml = limit ? '<p>' + escapeHtml(limit) + '</p>' : '';
      if ((result.limit === 'rest' || result.limit === 'absolute') && config.messages.floor_activity) {
        limitHtml += '<p>' + escapeHtml(config.messages.floor_activity) + '</p>';
      }
      refs.limit.innerHTML = limitHtml;
      refs.limit.hidden = !limitHtml;

      if (refs.macros) {
        refs.macros.hidden = !result.macros;
        if (result.macros) {
          ['protein', 'fat', 'carbs'].forEach(function (key) {
            var row = refs.macros.querySelector('[data-dc-macro="' + key + '"]');
            if (!row) return;
            row.querySelector('[data-dc-macro-g]').textContent = result.macros[key].g;
            row.querySelector('[data-dc-macro-pct]').textContent = result.macros[key].pct;
          });
          refs.macrosNote.hidden = !result.macros.adjusted;
          if (result.macros.adjusted) {
            refs.macrosNote.textContent = format(config.messages.macros_adjusted, { ref: result.macros.refWeight });
          }
        }
      }

      var notes = [];
      if (result.goal && result.goal.status === 'ok') {
        notes.push(format(config.messages.goal_weeks, { kg: kg(result.goal.kg), weeks: result.goal.weeks }));
      }
      if (result.goal && result.goal.status === 'too_low') {
        notes.push(format(config.messages.goal_too_low, { min: result.goal.minWeight, bmi: config.bmi_min }));
      }
      if (toNumber(input.age) >= config.senior_age) notes.push(format(config.messages.senior, { age: config.senior_age }));
      if (!result.macros && config.messages.protein) notes.push(config.messages.protein);
      refs.notes.innerHTML = notes.map(function (n) { return '<p>' + escapeHtml(n) + '</p>'; }).join('');
      if (config.protein_link) {
        refs.notes.innerHTML += '<p><a href="' + escapeHtml(config.protein_link) + '">' + escapeHtml(config.messages.protein_link) + '</a></p>';
      }
      return true;
    }

    function publish(result, input) {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      try {
        analytics.publish('deficit_calc_submit', {
          status: result.status,
          reason: result.reason || null,
          limit: result.limit || null,
          pace: input.pace,
          activity: input.activity,
          has_goal: !!toNumber(input.goal),
          age_band: result.status === 'ok' || result.status === 'no_room' ? ageBand(toNumber(input.age), config) : null
        });
      } catch (e) {}
    }

    function scrollToOutput() {
      var header = document.querySelector('.shopify-section-group-header-group');
      var top = (header ? header.getBoundingClientRect().bottom : 0) + 16;
      var rect = output.getBoundingClientRect();
      if (rect.top >= top && rect.top <= window.innerHeight * 0.4) return;
      window.scrollBy({ top: rect.top - top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    // Reason: a short, honest "showing the work" beat (2.2 s in all) makes the figure feel computed, not looked up.
    function showLoading(id, done) {
      var steps = refs.loading ? refs.loading.querySelectorAll('[data-dc-step]') : [];
      if (!steps.length) return done();
      var settleMs = reduceMotion ? 0 : 320;
      var stepMs = reduceMotion ? 0 : (2200 - settleMs) / steps.length;
      output.hidden = false;
      output.classList.remove('is-in');
      output.setAttribute('aria-busy', 'true');
      if (refs.placeholder) refs.placeholder.hidden = true;
      [refs.ok, refs.noRoom].concat(Array.prototype.slice.call(refs.blocked)).forEach(function (node) { node.hidden = true; });
      Array.prototype.forEach.call(steps, function (step) { step.classList.remove('is-done'); });
      refs.loading.style.setProperty('--dc-load', (steps.length * stepMs) + 'ms');
      refs.loading.hidden = false;
      output.focus({ preventScroll: true });
      scrollToOutput();
      var i = 0;
      (function tick() {
        if (id !== run) return;
        if (i < steps.length) {
          window.setTimeout(function () {
            if (id !== run) return;
            steps[i++].classList.add('is-done');
            tick();
          }, stepMs);
          return;
        }
        window.setTimeout(function () {
          if (id !== run) return;
          refs.loading.hidden = true;
          output.removeAttribute('aria-busy');
          done();
        }, settleMs);
      })();
    }

    function countUp(node, value) {
      if (reduceMotion || !window.requestAnimationFrame) return;
      var start = null;
      var from = Math.round(value * 0.82);
      (function frame(now) {
        if (start === null) start = now;
        var t = Math.min((now - start) / 650, 1);
        var eased = 1 - Math.pow(2, -10 * t);
        node.textContent = kcal(t < 1 ? Math.round((from + (value - from) * eased) / 10) * 10 : value);
        if (t < 1) window.requestAnimationFrame(frame);
      })(window.performance ? window.performance.now() : 0);
    }

    // Reason: cart attributes travel to the order, so the coach reads the result before the call.
    // Empty values clear attributes left by a previous calculation.
    function callAttributes(result, input) {
      var goal = toNumber(input.goal);
      var attributes = {
        'Calculateur': 'Déficit calorique',
        'Sexe': { female: 'Femme', male: 'Homme' }[input.sex] || '',
        'Âge': input.age || '',
        'Taille (cm)': input.height || '',
        'Poids (kg)': input.weight ? kg(input.weight) : '',
        'Cible (kcal/jour)': kcal(result.target),
        'Maintien (kcal/jour)': kcal(result.maintenance),
        'Déficit (kcal/jour)': kcal(result.applied) + ' (' + result.pct + ' %)',
        'Plancher (kcal/jour)': kcal(result.floor),
        'Rythme choisi': result.pace.label || kg(result.pace.kg) + ' kg/sem.',
        'Rythme retenu (kg/sem.)': kg(result.weekly),
        'Activité': result.activity.label || String(result.activity.pal),
        'Limite appliquée': { cap: 'Déficit plafonné', rest: 'Métabolisme de repos', absolute: 'Minimum absolu' }[result.limit] || '',
        'Objectif (kg à perdre)': goal ? kg(goal) : '',
        'Message': refs.callMessage ? refs.callMessage.value.trim() : ''
      };
      // Reason: clear what the protein calculator may have left in the same cart.
      ['Profil', 'Régime', 'Repas', 'Plancher (g/jour)', 'Cible (g/jour)', 'Par repas (g)', 'Poids de référence (kg)'].forEach(function (key) {
        attributes[key] = '';
      });
      return attributes;
    }

    function publishCall(input) {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      try {
        analytics.publish('calc_call_click', { calc: 'deficit', pace: input.pace, activity: input.activity });
      } catch (e) {}
    }

    if (refs.callLink) {
      refs.callLink.addEventListener('click', function (event) {
        if (refs.callLink.hasAttribute('data-dc-call-direct')) {
          checkoutAfterBooking();
          if (!last) return;
          saveAttributes(callAttributes(last.result, last.input)).catch(function () {});
          return publishCall(last.input);
        }
        if (!last || !window.fetch || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        var href = refs.callLink.href;
        var left = false;
        var go = function () {
          if (left) return;
          left = true;
          window.location.href = href;
        };
        publishCall(last.input);
        saveAttributes(callAttributes(last.result, last.input)).then(go, go);
        window.setTimeout(go, 1500);
      });
    }

    function reveal(field) {
      var vv = window.visualViewport;
      var header = document.querySelector('.shopify-section-group-header-group');
      var top = (header ? header.getBoundingClientRect().bottom : 0) + 12;
      var bottom = (vv ? vv.offsetTop + vv.height : window.innerHeight) - 12;
      var rect = field.getBoundingClientRect();
      if (rect.top >= top && rect.bottom <= bottom) return;
      window.scrollBy({ top: rect.top - top - Math.max(0, (bottom - top - rect.height) / 3), behavior: 'smooth' });
    }

    form.addEventListener('focusin', function (event) {
      var field = event.target;
      if (!field.matches('.pc-input, .pc-select')) return;
      var vv = window.visualViewport;
      var done = false;
      var run = function () {
        if (done) return;
        done = true;
        if (vv) vv.removeEventListener('resize', run);
        if (document.activeElement === field) reveal(field);
      };
      if (vv) vv.addEventListener('resize', run);
      window.setTimeout(run, 350);
    });

    form.addEventListener('keydown', function (event) {
      var target = event.target;
      if (event.key !== 'Enter' || !target.matches('input[type="number"]')) return;
      var controls = Array.prototype.filter.call(form.querySelectorAll('input:not([type="hidden"]), select, button'), function (c) {
        if (c.disabled) return false;
        if (c.type !== 'radio') return true;
        var group = form.querySelector('input[name="' + c.name + '"]:checked') || form.querySelector('input[name="' + c.name + '"]');
        return c === group;
      });
      var next = controls[controls.indexOf(target) + 1];
      if (!next || next.type === 'submit') return;
      event.preventDefault();
      next.focus({ preventScroll: true });
      reveal(next.closest('.pc-choice') || next);
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var input = read();
      var result = compute(input, config);
      var id = ++run;
      if (result.status === 'invalid') {
        render(result, input);
        var firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }
      showErrors({});
      showLoading(id, function () {
        render(result, input);
        submitted = true;
        publish(result, input);
        output.classList.add('is-in');
        if (result.status === 'ok') countUp(refs.target, result.target);
        output.focus({ preventScroll: true });
      });
    });

    form.addEventListener('change', function () {
      if (!submitted || output.hasAttribute('aria-busy')) return;
      var input = read();
      render(compute(input, config), input);
    });
  }

  var api = { compute: compute, validate: validate, bmr: bmr, macros: macros };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.OwlDeficitCalculator = api;

  if (typeof document !== 'undefined') {
    var boot = function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-deficit-calculator]'), init);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }
})(typeof window !== 'undefined' ? window : globalThis);
